// =============================================================================
// GAME MENU - Menu overlay for navigation
// =============================================================================

import { motion, AnimatePresence } from 'framer-motion';

interface GameMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onGoHome: () => void;
  onViewRules?: () => void;
  onCreateGame?: () => void;
  isInGame?: boolean;
}

export function GameMenu({
  isOpen,
  onClose,
  onGoHome,
  onViewRules,
  onCreateGame,
  isInGame = false,
}: Readonly<GameMenuProps>) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Menu Panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="glass-card p-6 m-4 relative">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-lg hover:bg-war-primary/10 transition-colors"
                aria-label="Close menu"
              >
                <svg
                  className="w-5 h-5 text-war-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Header */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-war-primary tracking-wide uppercase">
                  Меню
                </h2>
              </div>

              {/* Menu Items */}
              <div className="space-y-3">
                {isInGame && (
                  <button
                    onClick={() => {
                      onClose();
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-war-primary/10 hover:bg-war-primary/20 
                             text-war-primary font-semibold transition-all duration-200
                             border border-war-primary/30 hover:border-war-primary/50
                             text-left"
                  >
                    Відновити командування
                  </button>
                )}

                <button
                  onClick={() => {
                    onGoHome();
                    onClose();
                  }}
                  className="w-full px-4 py-3 rounded-lg bg-war-dim/20 hover:bg-war-dim/30 
                           text-war-primary font-semibold transition-all duration-200
                           border border-war-dim/30 hover:border-war-dim/50
                           text-left"
                >
                  {isInGame ? 'На головну' : 'Головна'}
                </button>

                {onViewRules && (
                  <button
                    onClick={() => {
                      onViewRules();
                      onClose();
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-war-dim/20 hover:bg-war-dim/30 
                             text-war-primary font-semibold transition-all duration-200
                             border border-war-dim/30 hover:border-war-dim/50
                             text-left"
                  >
                    Правила гри
                  </button>
                )}

                {onCreateGame && (
                  <button
                    onClick={() => {
                      onCreateGame();
                      onClose();
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-war-dim/20 hover:bg-war-dim/30 
                             text-war-primary font-semibold transition-all duration-200
                             border border-war-dim/30 hover:border-war-dim/50
                             text-left"
                  >
                    Створити сценарій
                  </button>
                )}
              </div>

              {/* Info */}
              {isInGame && (
                <p className="mt-6 text-sm text-war-dim">
                  Ваш прогрес автоматично збережено
                </p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
